import { useDialogPortal, useIsWhale, useLocalStorage } from "@/hooks";
import { useRef } from "react";
import { useMemo, useEffect } from "react";
import ga4 from "react-ga4";
import posthog from "posthog-js";
import { PostHogEvent, PostHogAction } from "@/posthog/posthog-capture-names";
import { safaryTrackWhaleDialogOpen } from "@/services/analytics/safary.service";
import { useAccount } from "wagmi";
import { useTrackingConsent } from "@/hooks/shared/tracking-consent/useTrackingConsent";
import { useUserTvl } from "@/hooks/infinifi/useUserTvl";

const validateCooldown = ({ lastDisplayed }: { lastDisplayed: Date | null }) => {
    if (lastDisplayed instanceof Date && !Number.isNaN(lastDisplayed.getTime())) {
        const now = new Date();
        const timeSinceLastOpened = now.getTime() - lastDisplayed.getTime();
        const oneWeekInMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
        if (timeSinceLastOpened < oneWeekInMs) return false; // Only show once a week
    }

    return true;
};

export const useWhaleRetentionCTA = () => {
    const { openDialog } = useDialogPortal();
    const { address } = useAccount();
    const { canTrack } = useTrackingConsent();

    const [lastOpen, setLastOpen, removeLastOpen] = useLocalStorage<number>("whale-retention-cta-last-open", 0);

    const { userTotalTvl, isLoading: isUserTvlLoading } = useUserTvl();
    const { isWhale, isLoading: isWhaleLoading, isPending: isWhalePending } = useIsWhale();

    const canDisplay = useMemo(
        () =>
            !isUserTvlLoading &&
            !isWhaleLoading &&
            !isWhalePending &&
            isWhale === true &&
            validateCooldown({
                lastDisplayed: lastOpen ? new Date(lastOpen) : null,
            }),
        [isUserTvlLoading, isWhale, isWhaleLoading, isWhalePending, lastOpen],
    );

    useEffect(() => {
        if (!canDisplay) return;
        if (!address) return;

        // Use ref to prevent multiple opens
        const hasOpenedRef = useRef(false);
        if (hasOpenedRef.current) return;
        hasOpenedRef.current = true;

        try {
            // Open dialog if user is a whale
            openDialog({
                dialogType: "WHALE_RETENTION",
                dialogProps: {},
            });
            setLastOpen(Date.now());
        } catch (error) {
            console.error("Error opening Whale Retention dialog:", error);
            removeLastOpen();
            hasOpenedRef.current = false;
            return;
        }

        try {
            // ga track
            canTrack &&
                ga4.event("whale_retention_dialog_opened", {
                    event_category: "engagement",
                    event_label: "Whale Retention Dialog Opened",
                    ...(isUserTvlLoading ? {} : { value: userTotalTvl }),
                });
        } catch (error) {
            console.error("GA4 tracking error:", error);
        }

        try {
            // posthog track
            canTrack &&
                posthog.capture(PostHogEvent.WhaleRetentionDialog.opened, {
                    action: PostHogAction[PostHogEvent.WhaleRetentionDialog.opened],
                    ...(isUserTvlLoading ? {} : { value: userTotalTvl }),
                });
        } catch (e) {
            console.error("PostHog tracking error:", e);
        }

        try {
            // safary track
            canTrack &&
                !isUserTvlLoading &&
                safaryTrackWhaleDialogOpen({
                    userTvl: userTotalTvl,
                    walletAddress: address,
                });
        } catch (e) {
            console.error("Safary tracking error:", e);
        }
    }, [canDisplay, openDialog, setLastOpen, removeLastOpen, userTotalTvl, address, canTrack, isUserTvlLoading]);

    return {
        canDisplay,
        setLast: setLastOpen,
        removeLast: removeLastOpen,
    };
};
